import { Component, effect, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { $state } from 'app/ajs-upgraded-providers';
import { ConfigurationManagerService, ConnectionService, EntityManagerService, LoaderService, ToolbarEventsService } from 'src/app/core';
import { ComponentDetailsFormService } from './component-details-form.service';
import { ToolbarEventsContextService } from 'src/app/core/services/toolbar-events/toolbar-events-context.service';
import { AlertDialogService, ConfirmDialogService, GenericJsonDetailDialogService } from 'src/app/dialogs';
import { AlternativeTranslate, Components } from 'tilby-models';
import { Subject, takeUntil } from 'rxjs';
import { ComponentsCategoriesFields, ComponentsFormGroups } from '../components.models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ItemDataOperationsService, TranslationsInputData } from 'src/app/features/items/services/item-data-operations.service';

@Component({
  selector: 'app-component-details',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatCheckboxModule,
    MatIconModule, MatButtonModule, TrimInputDirective,
    MatTooltipModule
  ],
  templateUrl: './component-details.component.html',
  styleUrl: './component-details.component.scss'
})
export class ComponentDetailsComponent implements OnInit, OnDestroy {

  readonly componentFormService = inject(ComponentDetailsFormService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly toolbarEventsContextService = inject(ToolbarEventsContextService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly connection = inject(ConnectionService);
  private readonly translate = inject(TranslateService);
  private readonly loaderService = inject(LoaderService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly state = inject($state);
  private readonly itemDataOperationsService = inject(ItemDataOperationsService);

  protected readonly hasToolbarMenu = this.configurationManagerService.isUserPermittedOptIn("show_developer_options");
  protected isAlternativeTranslateEnabled = !!this.configurationManagerService.getPreference('items.enable_alternative_translate');

  @Input() component!: Components;
  componentsList: Components[] = [];
  
  private destroy$ = new Subject<void>(); 

  isNew: boolean = false;
  componentForm!: ComponentsFormGroups;
  
  isReady = this.itemDataCatalogService.isReady;
  componentFormReady = this.componentFormService.componentFormReady;

  constructor() {

    effect(() => {
      if(this.isReady()) {
        this.componentsList = this.itemDataCatalogService.getDataByKey("components") || [];
        this.componentForm = this.componentFormService.createForm(this.component);
        this.loaderService.forceHide();
      }
    }, { allowSignalWrites: true })

    effect(() => {
      if(this.componentFormReady()) {
        this.listenToComponentNameChanges();
      }
    })
  }

  ngOnInit(): void {
    this.isNew = !this.component?.id;
    this.createToolbarButtons();
    this.initContextToolbarLabel();
  }

  initContextToolbarLabel() {
    if(this.isNew) {
      this.toolbarEventsContextService.label = `ITEMS.COMPONENTS.DETAILS.NAME_PLACEHOLDER`;
    } else {
      this.toolbarEventsContextService.label = ( this.component.name || "" ) + '\u200b';
    }
  }
  
  private listenToComponentNameChanges() {
    this.componentFormService.name?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.toolbarEventsContextService.label = value? value + '\u200b': `ITEMS.COMPONENTS.DETAILS.NAME_PLACEHOLDER`;
    })
  }

  checkboxOptionSelectionChange(event: MatCheckboxChange) {
    if(event.source?.value == '0') {
      if(event.checked) {
        this.componentFormService.categoriesArray?.controls.forEach(cat => {
          cat.get('checked')?.setValue(true);
        })
      } else {
        this.componentFormService.categoriesArray?.controls.forEach(cat => {
          cat.get("checked")?.setValue(false);
        })
      }
    } else {
      const allSelected = this.componentFormService.categoriesArray?.value.every((cat: ComponentsCategoriesFields, index: number) => index == 0 || cat.checked);
      if(allSelected) {
        this.componentFormService.categoriesArray?.at(0).get('checked')?.setValue(true, {emitEvent: false})
      } else {
        this.componentFormService.categoriesArray?.at(0).get('checked')?.setValue(false, {emitEvent: false})
      }
    }
  }
  
  /**
   * Create buttons for the toolbar
   */
  createToolbarButtons = () => {
    this.toolbarEventsContextService.backButton$.next({
        isIt: signal(true),
        name: 'arrow_back',
        icon: signal('arrow_back'),
        click: () => this.back()
    });

    this.toolbarEventsContextService.buttons$.next({
        barButtons: [
            {
                isIt: () => true,
                name: 'save',
                icon: signal('check'),
                click: _ => {
                  this.save();
                }
            },
            {
                isIt: () => true,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                click: _ => {
                  this.restore();
                }
            },
        ],
        panelButtons: []
    });

    this.toolbarEventsService.buttons$.next({
      barButtons: [
        {
          isIt: () => this.connection.isOffline(),
          name: 'cloud_off',
          icon: signal('cloud_off'),
          click: _ => null
        }
      ],
      panelButtons: [
        {
          isIt: signal(this.hasToolbarMenu),
          name: 'show_json_component',
          icon: signal('data_object'),
          label: signal('ITEMS.COMPONENTS.ACTIONS.SHOW_JSON'),
          click: _ => this.openComponentJsonDetail()
        }
      ]
    })
  }
  
  async openComponentJsonDetail() {
    await this.genericJsonDetailDialog.openDialog(
      { data: 
        { title:'ITEMS.COMPONENTS.ACTIONS.SHOW_JSON', 
        nameFile: 'item_json_data', 
        json: this.componentFormService.getComponentFields(this.component) } 
    });
  }
  
  back() {
    this.state.go(`app.new.items.components`);
    this.toolbarEventsContextService.buttons$.next({panelButtons: [], barButtons: []});
  }

  async restore() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.COMPONENTS.DETAILS.UNDO_CHANGES',
      }
    });

    if(!confirm) return;
    
    this.componentFormService.resetFormValues(this.component);
  }

  async save() {
    const componentAlreadyExist = this.componentsList.find((c: Components) => 
      this.componentFormService.name?.value === c.name && this.componentFormService.id?.value !== c.id
    )
    
    if (componentAlreadyExist) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.COMPONENTS.COMPONENT_ALREAD_EXISTING')
        }})
      return;
    }

    if(this.componentFormService.form.invalid) {
      this.componentFormService.form.markAllAsTouched();
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FORM_ERRORS.THERE_ARE_ERRORS')
        }})
      return;
    }

    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.COMPONENTS.DETAILS.WANT_TO_SAVE',
      }
    });

    if(!confirm) return;

    this.loaderService.show();

    const componentToSend = {
      ...this.componentFormService.getComponentFields(this.component),
      ...(this.component?.id && {id: this.component.id})
    } as Components;

    try {
      if(componentToSend.id) {
        this.component = await this.entityManager.components.putOneOnline(componentToSend);
      } else {
        this.component = await this.entityManager.components.postOneOnline(componentToSend);
      }

      // Aggiornamento lista ingredienti
      // const currentComponents = this.itemDataCatalogService.getDataByKey('components') || [];

      // if (componentToSend.id) {
      //   const index = currentComponents.findIndex(c => c.id === this.component.id);
      //   if (index !== -1) {
      //     currentComponents[index] = this.component;
      //   }
      // } else {
      //   currentComponents.push(this.component);
      // }

      // this.itemDataCatalogService.addDataToCatalog('components', currentComponents);

      this.state.go(`app.new.items.components`);

      this._snackBar.open(this.translate.instant('ITEMS.COMPONENTS.SAVED'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (e) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: componentToSend.id ? 'ITEMS.COMPONENTS.ERROR_IN_EDIT' : 'ITEMS.COMPONENTS.ERROR_IN_CREATION'
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }
    
  manageAlternativeTranslate() {
    const config: TranslationsInputData = {
      mainValue: this.componentFormService.name?.value,
      currentValues: this.componentFormService.alternativeNames?.value || [],
      maxLength: this.componentFormService.validators.nameMaxLength
    }
    this.itemDataOperationsService.getUpdatedTranslations(config).then((updatedValues: AlternativeTranslate[] | null) => {
      if(updatedValues) this.componentFormService.alternativeNames?.setValue(updatedValues);
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.componentFormReady.set(false);
  }
}
